import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

const PROBLEMS = ['whoOwes', 'splitFight', 'noClarity', 'noGoals'];
const PROBLEM_ICONS = ['😤', '💸', '🙈', '😞'];

function MockupSharedBalance() {
  const [view, setView] = useState('shared'); // shared | individual

  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 p-5 w-full max-w-xs select-none">
      {/* Toggle */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 mb-3">
        {['shared', 'individual'].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex-1 text-[9px] font-semibold py-1 rounded-md transition-all ${
              view === v
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-400'
            }`}
          >
            {v === 'shared' ? 'Compartido' : 'Individual'}
          </button>
        ))}
      </div>

      {view === 'shared' ? (
        <>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">
            Balance compartido
          </div>
          <div className="text-2xl font-extrabold text-gray-900 dark:text-white mb-4">
            $4,850,000
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-[9px] font-bold text-white">FR</div>
                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300">Francisco</span>
              </div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">$3,500,000</div>
            </div>
            <div className="w-px h-10 bg-gray-200 dark:bg-gray-700" />
            <div className="flex-1 text-right">
              <div className="flex items-center gap-1.5 mb-1 justify-end">
                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300">Ana</span>
                <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center text-[9px] font-bold text-white">AN</div>
              </div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">$1,350,000</div>
            </div>
          </div>
          <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
            <div className="h-full bg-blue-500 rounded-l-full transition-all duration-500" style={{ width: '72%' }} />
            <div className="h-full bg-pink-500 rounded-r-full transition-all duration-500" style={{ width: '28%' }} />
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {[
            { name: 'Francisco', initials: 'FR', color: 'bg-blue-500', income: '$3,500,000', expenses: '$980,000', balance: '$2,520,000' },
            { name: 'Ana', initials: 'AN', color: 'bg-pink-500', income: '$1,350,000', expenses: '$620,000', balance: '$730,000' },
          ].map((p) => (
            <div key={p.name} className="rounded-xl bg-gray-50 dark:bg-gray-700/50 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className={`w-5 h-5 rounded-full ${p.color} flex items-center justify-center text-[8px] font-bold text-white`}>
                  {p.initials}
                </div>
                <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{p.name}</span>
              </div>
              <div className="grid grid-cols-3 gap-1 text-[9px]">
                <div>
                  <div className="text-gray-400">Ingresos</div>
                  <div className="font-bold text-emerald-500">{p.income}</div>
                </div>
                <div>
                  <div className="text-gray-400">Gastos</div>
                  <div className="font-bold text-red-400">{p.expenses}</div>
                </div>
                <div>
                  <div className="text-gray-400">Balance</div>
                  <div className="font-bold text-gray-700 dark:text-gray-200">{p.balance}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MockupSplits() {
  const [splitMode, setSplitMode] = useState('equal'); // equal | percentage | custom

  const splits = [
    {
      desc: 'Cena restaurante',
      total: 65000,
      icon: '🍽️',
      modes: { equal: [32500, 32500], percentage: [45500, 19500], custom: [40000, 25000] },
    },
    {
      desc: 'Supermercado',
      total: 120000,
      icon: '🛒',
      modes: { equal: [60000, 60000], percentage: [84000, 36000], custom: [70000, 50000] },
    },
    {
      desc: 'Netflix + Spotify',
      total: 28000,
      icon: '🎬',
      modes: { equal: [14000, 14000], percentage: [19600, 8400], custom: [14000, 14000] },
    },
  ];

  const fmt = (n) => `$${(n / 1000).toFixed(0)}k`;

  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 p-5 w-full max-w-xs select-none">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
          Splits
        </div>
        <div className="flex gap-0.5">
          {[
            { key: 'equal', label: '50/50' },
            { key: 'percentage', label: '70/30' },
            { key: 'custom', label: 'Custom' },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setSplitMode(m.key)}
              className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full transition-all ${
                splitMode === m.key
                  ? 'bg-pink-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {splits.map((s) => {
          const [me, partner] = s.modes[splitMode];
          return (
            <div key={s.desc} className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm">
                {s.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-gray-800 dark:text-gray-200 truncate">{s.desc}</div>
                <div className="flex gap-2 text-[9px] text-gray-400">
                  <span className="text-blue-500">Yo: {fmt(me)}</span>
                  <span className="text-pink-500">Ana: {fmt(partner)}</span>
                </div>
              </div>
              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400">{fmt(s.total)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MockupCoupleGoals() {
  const [goals, setGoals] = useState([
    { icon: '🏠', name: 'Casa propia', savedFr: 4000, savedAn: 3000, target: 20000 },
    { icon: '✈️', name: 'Vacaciones Europa', savedFr: 1400, savedAn: 1300, target: 4000 },
  ]);

  const addFunds = (i, who) => {
    setGoals((prev) =>
      prev.map((g, idx) => {
        if (idx !== i) return g;
        const total = g.savedFr + g.savedAn;
        if (total >= g.target) return g;
        const add = Math.min(500, g.target - total);
        return who === 'fr'
          ? { ...g, savedFr: g.savedFr + add }
          : { ...g, savedAn: g.savedAn + add };
      })
    );
  };

  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700 p-5 w-full max-w-xs select-none">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">
        Metas en pareja
      </div>
      <div className="space-y-4">
        {goals.map((g, i) => {
          const total = g.savedFr + g.savedAn;
          const pctFr = Math.round((g.savedFr / g.target) * 100);
          const pctAn = Math.round((g.savedAn / g.target) * 100);
          const pctTotal = Math.round((total / g.target) * 100);
          const done = total >= g.target;

          return (
            <div key={g.name}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">{g.icon}</span>
                  <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">{g.name}</span>
                </div>
                <span className={`text-[10px] font-semibold ${done ? 'text-emerald-500' : 'text-gray-400'}`}>
                  {done ? 'Logrado!' : `${pctTotal}%`}
                </span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
                <div className="h-full bg-blue-400 rounded-l-full transition-all duration-500" style={{ width: `${pctFr}%` }} />
                <div className={`h-full bg-pink-400 transition-all duration-500 ${done ? 'rounded-r-full' : ''}`} style={{ width: `${pctAn}%` }} />
              </div>
              <div className="flex justify-between items-center text-[9px] text-gray-400 mt-1">
                <span>${(total / 1000).toFixed(0)}k / ${(g.target / 1000).toFixed(0)}k</span>
                {!done && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => addFunds(i, 'fr')}
                      className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold hover:bg-blue-200 dark:hover:bg-blue-500/20 transition-colors"
                    >
                      +FR
                    </button>
                    <button
                      onClick={() => addFunds(i, 'an')}
                      className="px-1.5 py-0.5 rounded bg-pink-100 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 font-semibold hover:bg-pink-200 dark:hover:bg-pink-500/20 transition-colors"
                    >
                      +AN
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CoupleSection({ onCta }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [visibleFeatures, setVisibleFeatures] = useState([]);
  const sectionRef = useRef();
  const featuresRef = useRef([]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            setVisibleFeatures((prev) => prev.includes(idx) ? prev : [...prev, idx]);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    featuresRef.current.forEach((el) => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const features = [
    { key: 'sharedBalance', mockup: <MockupSharedBalance /> },
    { key: 'fairSplits', mockup: <MockupSplits /> },
    { key: 'coupleGoals', mockup: <MockupCoupleGoals /> },
  ];

  return (
    <section ref={sectionRef} id="couple" className="py-20 px-4 bg-gradient-to-b from-pink-50/50 to-white dark:from-pink-950/10 dark:to-gray-950">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-5xl mb-4 block">💕</span>
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 dark:text-white mb-4">
            {t('landing.couple_section.title')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('landing.couple_section.subtitle')}
          </p>
        </div>

        {/* Problem cards */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {PROBLEMS.map((key, i) => (
            <div
              key={key}
              className="rounded-2xl bg-white dark:bg-gray-900 border border-pink-100 dark:border-pink-900/30 p-5 text-center transition-all duration-500"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <span className="text-3xl mb-2 block">{PROBLEM_ICONS[i]}</span>
              <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                {t(`landing.couple_section.problems.${key}.title`)}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {t(`landing.couple_section.problems.${key}.description`)}
              </p>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="text-center mb-16">
          <div className="w-16 h-1 bg-gradient-to-r from-pink-500 to-accent-500 mx-auto rounded-full mb-6" />
          <h3 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 dark:text-white">
            {t('landing.couple_section.howTitle')}
          </h3>
        </div>

        {/* Feature rows with interactive mockups */}
        <div className="space-y-20">
          {features.map((feat, i) => (
            <div
              key={feat.key}
              ref={(el) => (featuresRef.current[i] = el)}
              data-index={i}
              className={`flex flex-col ${i % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 md:gap-12 transition-all duration-700 ${
                visibleFeatures.includes(i)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Text */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-100 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 text-xs font-semibold mb-3">
                  {t(`landing.couple_section.features.${feat.key}.badge`)}
                </div>
                <h4 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {t(`landing.couple_section.features.${feat.key}.title`)}
                </h4>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(`landing.couple_section.features.${feat.key}.description`)}
                </p>
              </div>

              {/* Mockup */}
              <div className={`flex-shrink-0 ${i % 2 === 0 ? 'animate-float' : 'animate-float-delayed'}`}>
                {feat.mockup}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button onClick={onCta} className="btn-primary text-lg py-4 px-10">
            {t('landing.couple_section.cta')}
          </button>
        </div>
      </div>
    </section>
  );
}
